import React, { useState } from 'react';
import { SYLLABLE_FAMILIES, SyllableItem } from '../../core/curriculum/curriculumData';
import { speechProvider } from '../../core/speech/speechProvider';
import { appRepository } from '../../core/storage/repository';
import { RaraMascot } from '../../components/mascot/RaraMascot';
import { BackIcon, BookIcon, PuzzleIcon, SpeakerIcon } from '../../components/icons/ReadingIcons';

interface SyllablesViewProps {
  onBack: () => void;
  onGoToWordBuilder?: () => void;
}

export const SyllablesView: React.FC<SyllablesViewProps> = ({ onBack, onGoToWordBuilder }) => {
  const [selectedFamily, setSelectedFamily] = useState(SYLLABLE_FAMILIES[0]);
  const [activeItem, setActiveItem] = useState<SyllableItem>(SYLLABLE_FAMILIES[0].items[0]);
  const [playingTarget, setPlayingTarget] = useState<string | null>(null);
  const [mascotMessage, setMascotMessage] = useState(
    `Sentuh huruf suku kata untuk bunyi suku kata, atau sentuh gambar untuk bunyi katanya!`
  );

  // 1. Melafalkan satu bunyi suku kata, termasuk token fonetik khusus jika diperlukan.
  const playOnlySyllable = async (item: SyllableItem) => {
    setActiveItem(item);
    setPlayingTarget(`syl-${item.syllable}`);
    setMascotMessage(`Suku kata: ${item.syllable}`);
    speechProvider.playChime('tap');
    appRepository.recordSyllableLearned(item.syllable);
    // Token fonetik membantu TTS membedakan bunyi BE dari BI tanpa menggabungkan kata.
    await speechProvider.speak(item.spokenSyllable ?? item.syllable.toLowerCase(), { rate: 0.85 });
    setPlayingTarget(null);
  };

  // 2. Melafalkan CONTOH KATA SAJA: e.g. "Baju", "Cumi", "Cincin"
  const playOnlyWord = async (item: SyllableItem) => {
    if (!item.exampleWord) return;

    setActiveItem(item);
    setPlayingTarget(`word-${item.exampleWord}`);
    setMascotMessage(`Contoh kata: ${item.exampleWord}`);
    speechProvider.playChime('tap');
    appRepository.recordSyllableLearned(item.syllable);
    await speechProvider.speak(item.exampleWord, { rate: 0.88 });
    setPlayingTarget(null);
  };

  // 3. Melafalkan SELURUH NARASI LENGKAP: e.g. "Cu! Contoh kata: Cumi." atau "Bi! Contoh kata: Bibir."
  const playFullNarration = async (item: SyllableItem) => {
    setActiveItem(item);
    setPlayingTarget(`all-${item.syllable}`);
    setMascotMessage(item.exampleWord ? `${item.syllable}! Contoh kata: ${item.exampleWord}.` : `${item.syllable}!`);
    speechProvider.playChime('tap');
    appRepository.recordSyllableLearned(item.syllable);
    const spokenSyllable = item.syllable.charAt(0).toUpperCase() + item.syllable.slice(1).toLowerCase();
    await speechProvider.speak(item.exampleWord ? `${spokenSyllable}! Contoh kata: ${item.exampleWord}.` : spokenSyllable);
    setPlayingTarget(null);
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFDF8] pb-12 select-none">
      {/* Top Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b-2 border-amber-100 px-4 py-3 flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 bg-amber-100 hover:bg-amber-200 active:scale-95 text-amber-900 font-bold px-4 py-2.5 rounded-2xl transition cursor-pointer"
        >
          <BackIcon className="w-6 h-6" />
          <span className="text-sm md:text-base">Kembali</span>
        </button>

        <h1 className="text-xl md:text-2xl font-display font-bold text-[#4BC27A] flex items-center gap-2">
          <BookIcon className="w-9 h-9" /> Belajar Suku Kata
        </h1>

        {onGoToWordBuilder && (
          <button
            onClick={onGoToWordBuilder}
            className="flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 active:scale-95 text-white font-bold text-xs md:text-sm px-3.5 py-2 rounded-2xl shadow cursor-pointer"
          >
            <PuzzleIcon className="w-6 h-6" /> Susun Kata
          </button>
        )}
      </div>

      <div className="max-w-4xl mx-auto w-full px-4 pt-4 flex-1 flex flex-col gap-5">
        {/* Mascot companion with clickable speech bubble */}
        <RaraMascot
          state={playingTarget ? 'speaking' : 'idle'}
          message={mascotMessage}
          size="sm"
          onBubbleClick={() => playFullNarration(activeItem)}
        />

        {/* Hero Card Suku Kata Aktif: Memiliki tombol mandiri untuk Suku Kata dan Kata */}
        <div className="bg-white rounded-3xl p-5 md:p-6 shadow-xl border-4 border-emerald-200 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Bagian Kiri: Suara Suku Kata Saja */}
            <div className="flex-1 w-full flex flex-col items-center bg-emerald-50/70 border-2 border-emerald-300 rounded-2xl p-4 text-center">
              <span className="text-xs font-extrabold text-emerald-800 uppercase tracking-wider mb-1">
                Bunyikan Suku Kata Saja:
              </span>
              <span className="text-5xl md:text-6xl font-black font-display text-emerald-600 my-1">
                {activeItem.syllable}
              </span>
              <button
                onClick={() => playOnlySyllable(activeItem)}
                className={`mt-2 flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm shadow transition active:scale-95 cursor-pointer ${
                  playingTarget === `syl-${activeItem.syllable}`
                    ? 'bg-emerald-600 text-white scale-105 ring-4 ring-emerald-200'
                    : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                }`}
              >
                <SpeakerIcon className="w-6 h-6" />
                <span>Dengar bunyi "{activeItem.syllable}"</span>
              </button>
            </div>

            {/* Simbol Pemisah / Penghubung */}
            <div className="text-2xl font-black text-emerald-300 hidden sm:block">➡️</div>

            {/* Bagian Kanan: Suara Contoh Kata Saja */}
            <div className="flex-1 w-full flex flex-col items-center bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 text-center">
              <span className="text-xs font-extrabold text-amber-800 uppercase tracking-wider mb-1">
                {activeItem.exampleWord ? 'Bunyikan Contoh Kata Saja:' : 'Latihan Tambahan:'}
              </span>
              {activeItem.exampleWord ? (
                <>
                  <div className="flex items-center gap-3 my-1">
                    <span className="text-4xl md:text-5xl">{activeItem.emoji}</span>
                    <span className="text-3xl md:text-4xl font-black font-display text-[#243047]">
                      {activeItem.exampleWord}
                    </span>
                  </div>
                  <button
                    onClick={() => playOnlyWord(activeItem)}
                    className={`mt-2 flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm shadow transition active:scale-95 cursor-pointer ${
                      playingTarget === `word-${activeItem.exampleWord}`
                        ? 'bg-amber-600 text-white scale-105 ring-4 ring-amber-200'
                        : 'bg-amber-500 hover:bg-amber-600 text-white'
                    }`}
                  >
                    <SpeakerIcon className="w-6 h-6" />
                    <span>Bunyikan "{activeItem.exampleWord}" Saja</span>
                  </button>
                </>
              ) : (
                <>
                  <span className="text-4xl md:text-5xl">{activeItem.emoji}</span>
                  <span className="text-3xl md:text-4xl font-black font-display text-[#243047] my-1">
                    Belajar
                  </span>
                  <span className="text-xs text-amber-800">Belum ada kata umum untuk contoh ini.</span>
                </>
              )}
            </div>
          </div>

          {/* Banner Narasi Lengkap: Bersuara "CU! Contoh kata: Cumi." */}
          <button
            onClick={() => playFullNarration(activeItem)}
            className={`w-full bg-gradient-to-r from-emerald-100 via-teal-100 to-emerald-100 hover:from-emerald-200 hover:to-teal-200 border-2 border-emerald-300 px-4 py-3 rounded-2xl flex items-center justify-between text-left shadow-xs transition active:scale-98 cursor-pointer ${
              playingTarget === `all-${activeItem.syllable}` ? 'ring-4 ring-emerald-300 scale-101' : ''
            }`}
            title="Sentuh untuk melafalkan seluruh narasi suku kata dan contoh kata"
          >
            <div className="flex items-center gap-3">
              <SpeakerIcon className="w-8 h-8 shrink-0" />
              <div>
                <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider">
                  Narasi Lengkap (Sentuh untuk Suara Semua):
                </div>
                <div className="font-display font-black text-[#243047] text-base md:text-lg">
                  {activeItem.exampleWord
                    ? `"${activeItem.syllable}! Contoh kata: ${activeItem.exampleWord}."`
                    : `"${activeItem.syllable}!"`}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-extrabold text-emerald-900 bg-white/90 px-3 py-1.5 rounded-xl shadow-2xs shrink-0">
              <SpeakerIcon className="w-6 h-6" />
              <span>Dengar Semua ▶</span>
            </div>
          </button>
        </div>

        {/* Consonant Selector Tabs */}
        <div>
          <div className="text-xs font-bold text-[#64748B] mb-2 uppercase tracking-wider">
            Pilih Awalan Konsonan:
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
            {SYLLABLE_FAMILIES.map(fam => {
              const isSelected = fam.consonant === selectedFamily.consonant;
              return (
                <button
                  key={fam.consonant}
                  onClick={() => {
                    setSelectedFamily(fam);
                    setActiveItem(fam.items[0]);
                    speechProvider.playChime('tap');
                    speechProvider.speak(fam.consonant, { rate: 0.85 });
                  }}
                  className={`h-12 w-12 shrink-0 rounded-2xl font-display font-extrabold text-xl transition-all flex items-center justify-center active:scale-90 cursor-pointer ${
                    isSelected
                      ? 'bg-[#4BC27A] text-white shadow-md scale-105 ring-4 ring-green-100'
                      : 'bg-white border-2 border-amber-200 text-[#243047] hover:bg-amber-50'
                  }`}
                >
                  {fam.consonant}
                </button>
              );
            })}
          </div>
        </div>

        {/* Syllable Cards Grid with Split Buttons for Syllable & Example Word */}
        <div className="bg-white rounded-3xl p-5 md:p-6 shadow-xl border-4 border-emerald-200">
          <div className="text-center mb-4">
            <h2 className="text-2xl md:text-3xl font-display font-black text-[#243047]">
              Keluarga Huruf <span className="text-[#4BC27A]">{selectedFamily.consonant}</span>
            </h2>
            <p className="text-xs md:text-sm text-[#64748B] mt-1">
              Sentuh kotak <strong className="text-emerald-700">huruf</strong> untuk bunyi suku kata, atau kotak <strong className="text-amber-700">gambar</strong> untuk bunyi katanya:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4 my-2">
            {selectedFamily.items.map(item => {
              const isActive = activeItem.syllable === item.syllable;
              const isSyllablePlaying = playingTarget === `syl-${item.syllable}`;
              const isWordPlaying = playingTarget === `word-${item.exampleWord}`;

              return (
                <div
                  key={item.syllable}
                  className={`flex flex-col rounded-3xl border-3 transition-all overflow-hidden ${
                    isActive
                      ? 'border-emerald-400 shadow-md ring-2 ring-emerald-200 bg-white'
                      : 'border-emerald-200 bg-emerald-50/40 hover:border-emerald-300 shadow-xs'
                  }`}
                >
                  {/* Bagian 1: Tombol Suara Suku Kata SAJA (e.g. "BA", "CU") */}
                  <button
                    onClick={() => playOnlySyllable(item)}
                    className={`flex flex-col items-center justify-center pt-3.5 pb-2.5 px-2 transition-all cursor-pointer active:scale-95 ${
                      isSyllablePlaying
                        ? 'bg-emerald-500 text-white shadow-inner'
                        : 'bg-emerald-50/90 hover:bg-emerald-100 text-emerald-950'
                    }`}
                    title={`Sentuh untuk bunyikan suku kata "${item.syllable}" saja`}
                  >
                    <span className="text-3xl md:text-4xl font-black font-display tracking-wide">
                      {item.syllable}
                    </span>
                    <span
                      className={`text-[10px] font-bold mt-1 px-2 py-0.5 rounded-full ${
                        isSyllablePlaying
                          ? 'bg-emerald-700 text-white'
                          : 'bg-emerald-200/80 text-emerald-900'
                      }`}
                    >
                      <><SpeakerIcon className="w-5 h-5 inline-block align-middle" /> Suku Kata</>
                    </span>
                  </button>

                  {/* Garis Pemisah Halus */}
                  <div className="border-t-2 border-dashed border-emerald-200" />

                  {/* Bagian 2: Tombol Suara Contoh Kata SAJA (e.g. 🦑 "Cumi", 👕 "Baju") */}
                  <button
                    onClick={() => playOnlyWord(item)}
                    className={`flex flex-col items-center justify-center pt-2.5 pb-3.5 px-2 transition-all cursor-pointer active:scale-95 ${
                      isWordPlaying
                        ? 'bg-amber-500 text-white shadow-inner'
                        : 'bg-amber-50/80 hover:bg-amber-100 text-amber-950'
                    }`}
                    title={`Sentuh untuk bunyikan contoh kata "${item.exampleWord}" saja`}
                  >
                    <span className="text-3xl md:text-4xl my-1 drop-shadow-xs transition-transform hover:scale-110">
                      {item.emoji}
                    </span>
                    <span
                      className={`text-xs md:text-sm font-extrabold ${
                        isWordPlaying ? 'text-white' : 'text-[#243047]'
                      }`}
                    >
                      {item.exampleWord}
                    </span>
                    <span
                      className={`text-[10px] font-bold mt-1 px-2 py-0.5 rounded-full ${
                        isWordPlaying
                          ? 'bg-amber-700 text-white'
                          : 'bg-amber-200/80 text-amber-900'
                      }`}
                    >
                      <><SpeakerIcon className="w-5 h-5 inline-block align-middle" /> Kata</>
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Syllable combination demo */}
        <div className="bg-amber-50 rounded-3xl p-5 md:p-6 border-2 border-amber-200 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-left">
            <h3 className="font-display font-extrabold text-base md:text-lg text-amber-900">
              💡 Tahukah Kamu?
            </h3>
            <p className="text-xs md:text-sm text-amber-800 mt-1 max-w-lg leading-relaxed">
              Suku kata bisa kita gabungkan menjadi kata utuh! Contohnya: <br />
              <strong className="text-sm md:text-base text-[#4F7CF7]">BA</strong> +{' '}
              <strong className="text-sm md:text-base text-[#4F7CF7]">JU</strong> ={' '}
              <strong className="text-sm md:text-base text-[#243047]">BAJU 👕</strong>.
            </p>
          </div>
          {onGoToWordBuilder && (
            <button
              onClick={onGoToWordBuilder}
              className="bg-[#4F7CF7] hover:bg-blue-600 active:scale-95 text-white font-bold px-5 py-2.5 rounded-2xl shadow-md flex items-center gap-2 whitespace-nowrap text-sm cursor-pointer"
            >
              <span>Ayo Susun Kata</span>
              <span>➡️</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
